/**
 * Form state management utilities for CreateAuditSessionDialog
 *
 * Extracted state management logic to reduce component complexity and improve reusability.
 */

import type { SampleFilters } from '@internal-audit/domain';

import { getDefaultStartDate, getDefaultEndDate, getInitialFilters } from './ValidationHelpers';

/**
 * Form state setters interface
 */
export interface FormStateSetters {
  setSessionName: (value: string) => void;
  setSessionDescription: (value: string) => void;
  setStartDate: (value: string) => void;
  setEndDate: (value: string) => void;
  setFilters: (value: SampleFilters) => void;
  setError: (value: string) => void;
  setDateError: (value: string) => void;
}

/**
 * Reset all form fields to their default values
 */
export const resetFormState = (setters: FormStateSetters): void => {
  setters.setSessionName('');
  setters.setSessionDescription('');
  setters.setStartDate(getDefaultStartDate());
  setters.setEndDate(getDefaultEndDate());
  setters.setFilters(getInitialFilters());
  setters.setError('');
  setters.setDateError('');
};

/**
 * Update filters with new values while preserving other filter properties
 */
export const updateFilters = (currentFilters: SampleFilters, setFilters: (value: SampleFilters) => void, updates: Partial<SampleFilters>): void => {
  setFilters({ ...currentFilters, ...updates });
};

/**
 * Handle filter option selection changes
 */
export const createFilterChangeHandler =
  (currentFilters: SampleFilters, setFilters: (value: SampleFilters) => void, filterKey: keyof SampleFilters) => (values: string[] | number[]) => {
    updateFilters(currentFilters, setFilters, { [filterKey]: values });
  };

/**
 * Handle severity level filter changes (converts strings to numbers)
 */
export const createSeverityFilterHandler = (currentFilters: SampleFilters, setFilters: (value: SampleFilters) => void) => (values: string[]) => {
  const severityNumbers = values.map(v => parseInt(v, 10));

  updateFilters(currentFilters, setFilters, { labelSeverities: severityNumbers });
};

/**
 * Create sample parameters for audit session creation
 */
export const createSampleParameters = (params: { communityId: string; startDate: string; endDate: string; filters: SampleFilters }) => {
  // Create start date at beginning of day in UTC (00:00:00.000Z)
  // The 'Z' suffix ensures the date is interpreted as UTC, not local time
  const startDateTime = new Date(`${params.startDate}T00:00:00.000Z`);

  // Create end date at end of day in UTC (23:59:59.999Z) to make it inclusive
  // The 'Z' suffix ensures the date is interpreted as UTC, not local time
  const endDateTime = new Date(`${params.endDate}T23:59:59.999Z`);

  return {
    communityId: params.communityId,
    samplingParameters: {
      confidenceLevel: 95,
      marginOfError: 5,
      populationProportion: 0.5,
      timeframe: {
        start: startDateTime.toISOString(),
        end: endDateTime.toISOString(),
      },
    },
    filters: {
      languages: params.filters.languages,
      topics: params.filters.topics,
      labels: params.filters.labels,
      labelSeverities: params.filters.labelSeverities,
      contentTypes: params.filters.contentTypes,
      customValues: params.filters.customValues && Object.keys(params.filters.customValues).length > 0 ? params.filters.customValues : undefined,
    },
  };
};
